"use client";

import {
  useEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";

type TagName =
  | "div"
  | "section"
  | "article"
  | "aside"
  | "header"
  | "footer"
  | "main"
  | "nav"
  | "span";

type FadeInProps<T extends TagName = "div"> = {
  children: ReactNode;
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, "children">;

export function FadeIn<T extends TagName = "div">({
  children,
  className = "",
  as,
  ...rest
}: FadeInProps<T>) {
  const Tag = as ?? "div";
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          el.classList.add("visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    // @ts-expect-error -- dynamic intrinsic element tag
    <Tag ref={ref} className={`fade-section ${className}`} {...rest}>
      {children}
    </Tag>
  );
}
